import { chain } from 'lodash';
import React, { useMemo } from 'react';
import { AnalysisColorPaletteGq } from '../../../graphql/projects/models/AnalysisColorPaletteGq';
import { projectsApiService } from '../../../graphql/projects/projectsApiService';
import VoxelSelect, { GenericSelectOption } from '../../voxel/Forms/VoxelSelect';

export interface VoxelSelectPaletteProps {
  setSelectedPalette: (palette: AnalysisColorPaletteGq) => void;
  selectedPalette?: AnalysisColorPaletteGq;
  labelPosition?: 'top' | 'left';
  disabled?: boolean;
}

export const VoxelSelectPalette: React.FC<VoxelSelectPaletteProps> = ({
  setSelectedPalette,
  selectedPalette,
  labelPosition = 'top',
  disabled,
}) => {
  const defaultValue: GenericSelectOption = { value: -1, title: 'Default' };

  // Get palettes
  const { data: palettes, query } =
    projectsApiService.queries.analysis.useGetAnalysisColorPalettes();

  // Palette options
  const paletteOptions = useMemo(() => {
    const themeNames = chain(palettes)
      .orderBy([(p) => p.title?.toLowerCase(), (p) => p.title, (p) => p.id])
      .map((p) => ({ value: p.id, title: p.title }))
      .push(defaultValue)
      .reverse()
      .value();
    return themeNames;
  }, [palettes]);

  const selectedPaletteOption: GenericSelectOption = useMemo(() => {
    const currentOption = {
      value: !!selectedPalette ? selectedPalette.id : defaultValue.value,
      title: !!selectedPalette ? selectedPalette.title : defaultValue.title,
    };
    return currentOption;
  }, [selectedPalette]);

  return (
    <VoxelSelect
      label={'Color palette'}
      labelPosition={labelPosition}
      options={paletteOptions}
      selectedOptionValue={selectedPaletteOption.value}
      setSelectedOption={(option: GenericSelectOption) => {
        const palette = palettes?.find((p) => p.id === option.value);
        setSelectedPalette(palette);
      }}
      disabled={disabled || !palettes?.length}
    />
  );
};

export default VoxelSelectPalette;
